import { pipe } from 'remeda';
import * as R from 'remeda';

/**
 * Risultato del parsing di un messaggio step/answer
 */
export interface ParsedStepAnswer {
  stepId: number;
  answer: string;
}

/**
 * Estrae stepId e answer da un messaggio nel formato "step :2", "answer:D"
 * Restituisce null se il formato non è valido o se stepId è -1
 */
export const parseStepFromMessage = (
  message: string
): ParsedStepAnswer | null => {
  if (typeof message !== 'string' || message.trim() === '') {
    return null;
  }

  const stepMatch = message.match(/^step\s*:(-?\d+)/);
  const answerMatch = message.match(/answer:(.+)$/);

  if (!stepMatch || !answerMatch) {
    return null;
  }

  const stepId = parseInt(stepMatch[1], 10);

  // Ignora step con id -1
  if (stepId === -1) {
    return null;
  }

  if (Number.isNaN(stepId)) {
    return null;
  }

  const answer = answerMatch[1].trim();

  if (answer === '') {
    return null;
  }

  return {
    stepId,
    answer,
  };
};

/**
 * Filtra i messaggi ignorando quelli con step id -1
 */
export const filterValidSessionMessages = (messages: string[]): string[] => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return pipe(
    messages,
    R.filter((msg): msg is string => typeof msg === 'string'),
    R.filter(msg => {
      const stepMatch = msg.match(/^step\s*:(-?\d+)/);
      if (stepMatch) {
        const stepId = parseInt(stepMatch[1], 10);
        return stepId !== -1;
      }
      return true;
    })
  );
};

/**
 * Estrae tutte le coppie step/answer valide da un array di messaggi
 * Gestisce sia messaggi combinati ("step :0, answer:si") che separati (["step :0", "answer:si"])
 */
export const extractStepAnswer = (messages: string[]): ParsedStepAnswer[] => {
  if (!Array.isArray(messages)) {
    return [];
  }

  // Prima prova con parseStepFromMessage (messaggi combinati)
  const combinedResults = pipe(
    messages,
    R.filter((msg): msg is string => typeof msg === 'string'),
    R.map(parseStepFromMessage),
    R.filter((parsed): parsed is ParsedStepAnswer => parsed !== null)
  );

  if (combinedResults.length > 0) {
    return combinedResults;
  }

  // Se non trova messaggi combinati, cerca messaggi separati
  const separatedResults: ParsedStepAnswer[] = [];
  let currentStepId: number | null = null;

  for (const msg of messages) {
    if (typeof msg !== 'string') {
      continue;
    }

    const stepMatch = msg.match(/^step\s*:(-?\d+)/);
    const answerMatch = msg.match(/^answer:(.+)$/);

    if (stepMatch) {
      const stepId = parseInt(stepMatch[1], 10);
      // Ignora step con id -1
      if (stepId !== -1 && !Number.isNaN(stepId)) {
        currentStepId = stepId;
      } else {
        currentStepId = null;
      }
    } else if (answerMatch && currentStepId !== null) {
      const answer = answerMatch[1].trim();
      if (answer !== '' && answer.toLowerCase() !== 'skip') {
        separatedResults.push({
          stepId: currentStepId,
          answer,
        });
      }
      currentStepId = null;
    }
  }

  return separatedResults;
};

/**
 * Risultato del parsing di un messaggio video/audio mancante
 */
export interface ParsedMediaMissing {
  type: 'video' | 'audio';
  id: string;
  name?: string;
}

/**
 * Estrae informazioni da messaggi tipo "Video ID: 94 Il ritardo" o "Audio ID: 123"
 */
export const parseMediaMissingMessage = (
  message: string
): ParsedMediaMissing | null => {
  if (typeof message !== 'string' || message.trim() === '') {
    return null;
  }

  // Pattern per "Video ID: 94 Il ritardo" o "Audio ID: 123"
  const videoMatch = message.match(/^Video\s+ID:\s*(\d+)\s*(.+)?$/i);
  if (videoMatch) {
    return {
      type: 'video',
      id: videoMatch[1],
      name: videoMatch[2]?.trim() || undefined,
    };
  }

  const audioMatch = message.match(/^Audio\s+ID:\s*(\d+)\s*(.+)?$/i);
  if (audioMatch) {
    return {
      type: 'audio',
      id: audioMatch[1],
      name: audioMatch[2]?.trim() || undefined,
    };
  }

  return null;
};

/**
 * Formatta un messaggio di media mancante in modo user-friendly
 */
export const formatMediaMissingMessage = (
  parsed: ParsedMediaMissing
): string => {
  const mediaType = parsed.type === 'video' ? 'video' : 'audio';
  const mediaTypeArticle = parsed.type === 'video' ? 'Il' : "L'";

  if (parsed.name) {
    if (parsed.type === 'audio') {
      return `${mediaTypeArticle}${mediaType} "${parsed.name}" (ID: ${parsed.id}) deve essere scaricato sul visore`;
    }
    return `${mediaTypeArticle} ${mediaType} "${parsed.name}" (ID: ${parsed.id}) deve essere scaricato sul visore`;
  }

  if (parsed.type === 'audio') {
    return `${mediaTypeArticle}${mediaType} con ID ${parsed.id} deve essere scaricato sul visore`;
  }
  return `${mediaTypeArticle} ${mediaType} con ID ${parsed.id} deve essere scaricato sul visore`;
};

/**
 * Trasforma un array di messaggi, formattando quelli di media mancante
 */
export const formatSessionMessages = (messages: string[]): string[] => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return pipe(
    messages,
    R.filter((msg): msg is string => typeof msg === 'string'),
    R.map(msg => {
      const parsed = parseMediaMissingMessage(msg);
      if (parsed) {
        return formatMediaMissingMessage(parsed);
      }
      return msg;
    })
  );
};

/**
 * Mappa un messaggio originale a un messaggio formattato
 * Restituisce [chiave, valore] se il messaggio è mappabile, altrimenti null
 */
export const mapMessageToFormatted = (
  message: string
): [string, string] | null => {
  if (typeof message !== 'string' || message.trim() === '') {
    return null;
  }

  // Pattern per "Video ID: 94 Il ritardo" o "Audio ID: 123"
  const videoMatch = message.match(/^Video\s+ID:\s*(\d+)\s*(.+)?$/i);
  if (videoMatch) {
    const originalMessage = message.trim();
    const formattedMessage = `è necessario scaricare la seguente risorsa sul visore "${originalMessage}" prima di procedere`;
    return [originalMessage, formattedMessage];
  }

  const audioMatch = message.match(/^Audio\s+ID:\s*(\d+)\s*(.+)?$/i);
  if (audioMatch) {
    const originalMessage = message.trim();
    const formattedMessage = `è necessario scaricare la seguente risorsa sul visore "${originalMessage}" prima di procedere`;
    return [originalMessage, formattedMessage];
  }

  return null;
};

/**
 * Mappa le coppie step/answer a messaggi formattati
 * Restituisce array di [chiave, valore] per ogni coppia step/answer valida
 * Ignora step -1 e answer "skip"
 * Gestisce sia messaggi combinati ("step :0, answer:si") che separati (["step :0", "answer:si"])
 */
export const mapStepAnswerPairToFormatted = (
  messages: string[]
): Array<[string, string]> => {
  if (!Array.isArray(messages)) {
    return [];
  }

  // extractStepAnswer gestisce già sia messaggi combinati che separati
  const stepAnswers = extractStepAnswer(messages);

  return stepAnswers
    .filter(({ answer }) => {
      // Ignora answer "skip" (case-insensitive)
      return answer.trim().toLowerCase() !== 'skip';
    })
    .map(({ answer }) => {
      // Usa "Utente ha interagito" come titolo
      const originalMessage = 'Utente ha interagito';
      const formattedMessage = `L'utente ha risposto "${answer}"`;
      return [originalMessage, formattedMessage] as [string, string];
    });
};

/**
 * Trasforma un array di messaggi in array di [chiave, valore]
 * Gestisce sia messaggi video/audio che step/answer
 * I messaggi non mappabili vengono ignorati
 */
export const mapMessagesToFormatted = (
  messages: string[]
): Array<[string, string]> => {
  if (!Array.isArray(messages)) {
    return [];
  }

  // Mappa i messaggi video/audio
  const mediaMapped = pipe(
    messages,
    R.filter((msg): msg is string => typeof msg === 'string'),
    R.map(mapMessageToFormatted),
    R.filter((mapped): mapped is [string, string] => mapped !== null)
  );

  // Mappa le coppie step/answer (esclude già "skip")
  const stepAnswerMapped = mapStepAnswerPairToFormatted(messages);

  // Combina entrambe le mappature
  return [...mediaMapped, ...stepAnswerMapped];
};
